import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { ConfirmPopup, ConfirmPopupModule } from 'primeng/confirmpopup';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { authUser } from '../../../../interfaces';
import { Goal } from '../../../../interfaces/models/goal.model';
import { Indicator } from '../../../../interfaces/models/indicator.model';
import { IProject, IProjectGovLinkItem, IProjectStrategicLinkItem, ProjectGovLinkTypesEnum } from '../../../../interfaces/models/project.model';
import { Pillar, Strategy } from '../../../../interfaces/models/strategy.model';
import { DirectionService } from '../../../../services/direction.service';
import { ProjectService } from '../../../../services/project.service';
import { SignalRService } from '../../../../services/signal-r.service';
import { SocketService } from '../../../../services/socket.service';
import { DateRangeComponent } from '../../../../shared/components/date-range/date-range.component';
import { WebViewerComponent } from '../../../../webviewer/webviewer.component';
import { filterActive } from '../../../../utils/filter-active';



@Component({
  selector: 'app-project',
  standalone: true,
  imports:
    [
      CommonModule,
      CalendarModule,
      InputTextareaModule,
      ButtonModule,
      AccordionModule,
      TableModule,
      FormsModule,
      ReactiveFormsModule,
      DropdownModule,
      InputTextModule,
      TooltipModule,
      ConfirmPopupModule,
      DateRangeComponent,
      TranslateModule,
      WebViewerComponent,
      DialogModule,
    ],
  providers: [ConfirmationService, DatePipe],
  templateUrl: './project.component.html',
  styleUrl: './project.component.scss'
})
export class ProjectComponent implements OnInit, OnDestroy {
  dir!: 'rtl' | 'ltr';
  isFileViewer = false;
  selectedFile!: any;
  minStartegyDate!: Date;
  maxStrategyDate!: Date;
  date_range = {} as any
  @Input() user = {} as authUser;
  private readonly subscriptions = new Subscription();
  _strategy!: Strategy;
  @Input() set strategy(value: Strategy) {
    this._strategy = value;
    console.log(this.strategy);
    this.minStartegyDate = new Date(value.start_date);
    this.maxStrategyDate = new Date(value.end_date);
    if (!this._strategy?.id) {
      return;
    }
  }
  _strategicRelations!: any;
  @Input() set strategicRelations(data: any) {
    if (data) {
      this.indicatorsOptions = filterActive(data['data']['indicators'] as Indicator[]);
      this.filterdIndicatorsOptions = this.indicatorsOptions;
      this.goalsOptions = filterActive(data['data']['goals'] as Goal[]);
      this.filterdGoalsOptions = this.goalsOptions;
      this.pillarsOptions = filterActive(data['data']['pillars'] as Pillar[]);
      this._strategicRelations = data;
    }


  }
  get strategy() {
    return this._strategy;
  }
  _project: IProject = {} as IProject;
  @Input() set project(value: IProject) {


    this._project = { ...value, created_at: value.created_at ? value.created_at : new Date().getTime() };
    if (value?.id > 0) {
      this.projectStrategicLinkItems = value?.strategicLinks.map((x: any) => {
        return {
          id: x.id,
          strategic_goal_name: x.goal_name,
          strategic_indicator_name: x.indicator_name,
          strategic_indicator_id: x.strategic_indicator_id,
          strategic_goal_id: x.strategic_goal_id
        } as IProjectStrategicLinkItem;
      });
      this.joinProject();
      const emirate_goal_item = value?.govLinks?.find(x => x.goal_type == ProjectGovLinkTypesEnum.emirate);
      const state_goal_item = value?.govLinks?.find(x => x.goal_type == ProjectGovLinkTypesEnum.state);
      this.selectedEmirateRef = emirate_goal_item;
      this.selectedStateRef = state_goal_item;
      const emirate_goal = {
        emirate_goal_description: emirate_goal_item?.goal_description || '',
        emirate_goal_reference: emirate_goal_item?.goal_reference || '',
      } as any;
      const state_goal = {
        state_goal_description: state_goal_item?.goal_description || '',
        state_goal_reference: state_goal_item?.goal_reference || ''
      } as any;
      //populating forms 
      const generalProjectInfoForm = {
        name: value.name || '',
        name_en: value.name_en || '',
        goals: value.goals || '',
        success_factors: value.success_factors || '',
        targets: value.targets || '',
        target_audience: value.target_audience || '',
        description: value.description || '',
        description_en: value.description_en || '',
        kpis: value.kpis || '',
        weight: Math.round(Number(value.weight) || 0),
        urgent_need: value.urgent_need || '',
        date_range: {
          start_date: new Date(value.start_date),
          end_date: new Date(value.end_date)
        }
      } as any;
      this.projectForm.setValue({
        ProjectGovLinkForm: { emirate_goal, state_goal },
        generalProjectInfoForm
      })
      this.date_range = {
        start_date: new Date(value.start_date),
        end_date: new Date(value.end_date)
      }
      if (!this.project.IS_ALLOWED_TO_UPDATE) {
        this.projectForm.disable();
      }
    }

  }
  get project() {
    return this._project;
  }
  @ViewChild('stateReferenceFileInput') stateReferenceFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('emirateReferenceFileInpute') emirateReferenceFileInpute!: ElementRef<HTMLInputElement>;
  @ViewChild(ConfirmPopup) confirmPopup!: ConfirmPopup;
  showEditConfirmDialog = false;
  pendingUpdateData: any = null;
  @ViewChild('goalAndIndicatorForm') goalAndIndicatorForm!: FormGroup;
  @Output() submitSuccess = new EventEmitter<{ projectId: number, strategyId: number }>();
  stateReferenceFile!: File;
  emirateReferenceFile!: File;
  projectStrategicLinkItem = {} as IProjectStrategicLinkItem;
  projectStrategicLinkItems = [] as IProjectStrategicLinkItem[]
  isLinkingFormSubmitted = false;
  isgoalAndIndicatorFormSubmitted = false;
  strategicLinkMap = new Map();
  indicatorsOptions!: Indicator[];
  filterdIndicatorsOptions!: Indicator[];
  goalsOptions!: Goal[];
  filterdGoalsOptions = [] as Goal[];
  pillarsOptions!: Pillar[];
  isProjectSubmitted = false;


  //for validation in the template
  get emirate_goal_description(): AbstractControl {
    return this.projectForm.get('ProjectGovLinkForm.emirate_goal.emirate_goal_description') as AbstractControl;
  }
  get emirate_goal_reference(): AbstractControl {
    return this.projectForm.get('ProjectGovLinkForm.emirate_goal.emirate_goal_reference') as AbstractControl;
  }
  get state_goal_description(): AbstractControl {
    return this.projectForm.get('ProjectGovLinkForm.state_goal.state_goal_description') as AbstractControl;
  }
  get state_goal_reference(): AbstractControl {
    return this.projectForm.get('ProjectGovLinkForm.state_goal.state_goal_reference') as AbstractControl;
  }
  get emirate_goal__group(): AbstractControl {
    return this.projectForm.get('ProjectGovLinkForm.emirate_goal') as AbstractControl;
  }

  get state_goal_group(): AbstractControl {
    return this.projectForm.get('ProjectGovLinkForm.state_goal') as AbstractControl;
  }

  // ==============general info ===================
  get name_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.name') as AbstractControl;
  }
  get weight_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.weight') as AbstractControl;
  }
  get goals_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.goals') as AbstractControl;
  }

  get success_factors_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.success_factors') as AbstractControl;
  }
  get targets_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.targets') as AbstractControl;
  }
  get description_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.description') as AbstractControl;
  }

  get kpis_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.kpis') as AbstractControl;
  }
  get urgent_need_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.urgent_need') as AbstractControl;
  }
  get start_date_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.date_range.start_date') as AbstractControl;
  }
  get end_date_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.date_range.end_date') as AbstractControl;
  }
  get date_range_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.date_range') as AbstractControl;
  }
  get target_audience_control(): AbstractControl {
    return this.projectForm.get('generalProjectInfoForm.target_audience') as AbstractControl;
  }

  onWeightKeyDown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    if ([46, 8, 9, 27, 13, 110, 190].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true) ||
      // Allow: home, end, left, right
      (event.keyCode >= 35 && event.keyCode <= 39)) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }

  onWeightInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^0-9.]/g, '');
    
    // Parse to number
    let numValue = parseFloat(value);
    
    // If value is empty or NaN, set to 0
    if (value === '' || isNaN(numValue)) {
      numValue = 0;
      input.value = '0';
      this.weight_control.setValue(0);
    }
    // If value is negative, set to 0
    else if (numValue < 0) {
      numValue = 0;
      input.value = '0';
      this.weight_control.setValue(0);
    }
    // If value is greater than 100, set to 100
    else if (numValue > 100) {
      numValue = 100;
      input.value = '100';
      this.weight_control.setValue(100);
    }
    // Update the form control value with the valid number
    else {
      // Round to nearest integer
      numValue = Math.round(numValue);
      input.value = numValue.toString();
      this.weight_control.setValue(numValue);
    }
    
    // Trigger validation
    this.weight_control.updateValueAndValidity();
  }

  projectForm = new FormGroup(
    {
      ProjectGovLinkForm: new FormGroup({
        emirate_goal: new FormGroup({
          emirate_goal_description: new FormControl(''),
          emirate_goal_reference: new FormControl(''),
        }, { validators: [this.oneOfTwoEmirateValidator as ValidatorFn] }),
        state_goal: new FormGroup({
          state_goal_description: new FormControl(''),
          state_goal_reference: new FormControl(''),
        }, { validators: [this.oneOfTwoStateValidator as ValidatorFn] })
      }),
      generalProjectInfoForm: new FormGroup({
        name: new FormControl('', [Validators.required]),
        name_en: new FormControl(''),
        weight: new FormControl(0, [Validators.min(0), Validators.max(100)]),
        goals: new FormControl(''),
        success_factors: new FormControl(''),
        targets: new FormControl(''),
        target_audience: new FormControl(''),
        description: new FormControl('', [Validators.required]),
        description_en: new FormControl(''),
        kpis: new FormControl('', [Validators.required]),
        urgent_need: new FormControl(''),
        date_range: new FormGroup({
          start_date: new FormControl('', [Validators.required]),
          end_date: new FormControl('', [Validators.required]),
        }, this.dateRangeValidator as ValidatorFn),

      })
    }
  )

  private dateRangeValidator(group: FormGroup) {
    const startDate = group.get('start_date')?.value;
    const endDate = group.get('end_date')?.value;
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      return { dateRangeInvalid: true };
    }
    return null;
  }
  constructor(
    private toastr: ToastrService,
    private confirmationService: ConfirmationService,
    private projectService: ProjectService,
    private datePipe: DatePipe,
    private router: Router,
    private directionService: DirectionService,
    private socket: SocketService,
    private signalRService: SignalRService
  ) {
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // this.leaveProject();
  }

  ngOnInit(): void {
    this.directionService.direction$.subscribe((dir) => {
      this.dir = dir;
    })

  }
  //sync validators
  oneOfTwoEmirateValidator(group: FormGroup): { [s: string]: boolean } | null {
    const controlOne = group.get('emirate_goal_description');
    const controlTwo = group.get('emirate_goal_reference');
    const input1 = controlOne?.value;
    const input2 = controlTwo?.value;
    if (!input1 && input2) {
      return { "EmirateOne": true }
    } else if (input1 && !input2) {
      return { "EmirateTwo": true }
    } else {
      return null;
    }
  }
  oneOfTwoStateValidator(group: FormGroup): { [s: string]: boolean } | null {
    const controlOne = group.get('state_goal_description');
    const controlTwo = group.get('state_goal_reference');
    const input1 = controlOne?.value;
    const input2 = controlTwo?.value;
    if (!input1 && input2) {
      return { "stateOne": true }
    } else if (input1 && !input2) {
      return { "stateTwo": true }
    } else {
      return null;
    }
  }



  //reactive form validator 
  dateNotOlderThanStrategyDate(control: FormControl): { [s: string]: boolean } | null {
    const controlDate = control?.value as Date;
    return null
  }


  // ===================
  addProjectStrategicLinkItem() {
    this.isgoalAndIndicatorFormSubmitted = true;
    const strategic_goal_id = this.goalAndIndicatorForm.value['strategic_goal_id']
    const strategic_indicator_id = this.goalAndIndicatorForm.value['strategic_indicator_id']

    const strategic_goal_name = this.goalsOptions.find(g => g.id === strategic_goal_id)?.name || '';
    const strategic_indicator_name = this.indicatorsOptions.find(i => i.id == strategic_indicator_id)?.name || '';


    if (this.goalAndIndicatorForm.valid) {
      const linkId = `${strategic_goal_id}-${strategic_indicator_id}`;
      const isUnique = this.strategicLinkMap.get(linkId);
      console.log("isUnique", isUnique);
      if (!isUnique) {
        this.strategicLinkMap.set(linkId, 1);
        this.projectStrategicLinkItems = [...this.projectStrategicLinkItems, {
          strategic_goal_id: strategic_goal_id,
          strategic_indicator_id: strategic_indicator_id,
          strategic_indicator_name, strategic_goal_name
        } as IProjectStrategicLinkItem];
      } else {

        this.toastr.warning(this.dir == 'rtl' ? "قد سبق الارتباط بنفس الهدف والمؤشر" : "A link with the same goal and indicator has already been added");
      }
    }
  }
  confirmValueDelete(event: Event, index: number) {
    const target = event.target as HTMLElement;
    this.confirmationService.confirm({
      target: target,
      message: "هل تريد تأكيد المسح؟",
      accept: () => {
        const StrategicLinkToDelete = this.projectStrategicLinkItems[index];
        this.projectStrategicLinkItems = this.projectStrategicLinkItems.map(x => {
          if (`${StrategicLinkToDelete.strategic_goal_id}-${StrategicLinkToDelete.strategic_indicator_id}` == `${x.strategic_goal_id}-${x.strategic_indicator_id}`) {
            return { ...StrategicLinkToDelete, is_deleted: true } as IProjectStrategicLinkItem
          } else {
            return x;
          }
        })
        console.log(this.projectStrategicLinkItems);
        StrategicLinkToDelete
        this.strategicLinkMap.delete(`${StrategicLinkToDelete.strategic_goal_id}-${StrategicLinkToDelete.strategic_indicator_id}`)
        this.toastr.success("تمت المسح ", "عملية ناجحة");
      },
      reject: () => {
        this.toastr.info("تم الالغاء", "الغاء");
      }
    });
  }
  accept() {
    this.confirmPopup.accept();
  }
  reject() {
    this.confirmPopup.reject();
  }
  triggerStateReferenceFileInput() {
    this.stateReferenceFileInput.nativeElement.click();
  }
  triggerEmirateReferenceFile() {
    this.emirateReferenceFileInpute.nativeElement.click();
  }

  selectedStateRef = {} as any;
  selectedEmirateRef = {} as any;
  viewMode = '';

  downloadStateReferenceFile() {
    this.projectService.downloadReferenceFile(
      this.strategy.id, this.project.id,
      this.selectedStateRef.reference_file.gov_linkage_id,
      this.selectedStateRef.reference_file.id).subscribe((x) => {
        const blob = new Blob([x], { type: x.type });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = "";
        anchor.click();
        window.URL.revokeObjectURL(url);
      });
  }

  downloadEmirateReferenceFile() {
    this.projectService.downloadReferenceFile(this.strategy.id, this.project.id,
      this.selectedEmirateRef.reference_file.gov_linkage_id,
      this.selectedEmirateRef.reference_file.id).subscribe((x) => {
        const blob = new Blob([x], { type: x.type });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = "";
        anchor.click();
        window.URL.revokeObjectURL(url);
      });
  }

  showStateReferenceFile() {
    this.isFileViewer = true;
    this.viewMode = 'state';
  }
  showEmirateReferenceFile() {
    this.isFileViewer = true;
    this.viewMode = 'emirate';
  }
  onStateReferenceFilechange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.stateReferenceFileMetaData = this.getFilesMetaData(input.files[0]);
      this.stateReferenceFile = input.files[0];
    }
  }


  emirateReferenceFileMetaData = {} as any;
  stateReferenceFileMetaData = {} as any;
  onEmirateReferencechange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.emirateReferenceFileMetaData = this.getFilesMetaData(input.files[0]);
      this.emirateReferenceFile = input.files[0];
    }
  }
  onProjectSubmit() {
    console.log('projectForm', this.projectForm);
    this.isProjectSubmitted = true;
    const generalProjectInfoFormValue = this.projectForm.value['generalProjectInfoForm']
    const emirate_goal = this.projectForm.get('ProjectGovLinkForm.emirate_goal')

    const govLinks_emirate = {
      goal_type: ProjectGovLinkTypesEnum.emirate,
      goal_description: emirate_goal?.value.emirate_goal_description,
      goal_reference: emirate_goal?.value?.emirate_goal_reference,
      reference_file: this.emirateReferenceFile ? true : false,
    } as IProjectGovLinkItem



    const state_goal = this.projectForm.get('ProjectGovLinkForm.state_goal')
    const govLinks_State = {
      goal_type: ProjectGovLinkTypesEnum.state,
      goal_description: state_goal?.value?.state_goal_description,
      goal_reference: state_goal?.value?.state_goal_reference,
      reference_file: this.stateReferenceFile ? true : false,
    } as IProjectGovLinkItem




    let govLinks = [];
    const [govLinks_emirate_is_empty, govLinks_State_is_empty] = [
      this.isGovLinkEmpty(govLinks_emirate),
      this.isGovLinkEmpty(govLinks_State)
    ]


    if (!govLinks_emirate_is_empty) govLinks.push(govLinks_emirate);
    if (!govLinks_State_is_empty) govLinks.push(govLinks_State);
    const strategicLinks = this.projectStrategicLinkItems.map(x => {
      return {
        ...x,
        strategic_goal_id: x.strategic_goal_id,
        strategic_indicator_id: x.strategic_indicator_id,
        is_deleted: x?.is_deleted ? true : false
      }
    });




    // Ensure at least one strategic link is provided
    if (!strategicLinks.length) {
      this.toastr.error(this.dir == 'rtl' ? 'أضف ارتباطاً استراتيجياً واحداً على الأقل' : 'Add at least one strategic link');
      return;
    }

    let formData: any = new FormData();
    // Use keys expected by API DTO ([FromForm] CreateProjectDTO): strategicLinks, govLinks
    formData.append("govLinks", JSON.stringify(govLinks))
    formData.append("strategicLinks", JSON.stringify(strategicLinks))
    //files
    formData.append("emirateReferenceFile", this.emirateReferenceFile)
    formData.append("stateReferenceFile", this.stateReferenceFile)
    
    // Fields that backend requires but we want to make optional in frontend
    const optionalFieldsForBackend = ['targets', 'urgent_need', 'success_factors', 'target_audience', 'goals'];
    
    Object.entries(generalProjectInfoFormValue as any).forEach(([key, value]) => {

      if (key == 'date_range') {
        let dateRange = value as any;
        formData.append('start_date', this.datePipe.transform(new Date(dateRange['start_date']), 'MM/dd/yyyy'))
        formData.append('end_date', this.datePipe.transform(new Date(dateRange['end_date']), 'MM/dd/yyyy'))
      } else if (key === 'weight') {
        const numericWeight = Number(value);
        const safeWeight = Number.isFinite(numericWeight)
          ? Math.max(0, Math.min(100, Math.round(numericWeight)))
          : 0;
        formData.append(key, String(safeWeight));
      } else {
        // If field is optional and empty, send "." to satisfy backend validation
        const stringValue = value?.toString() || '';
        const fieldValue = (optionalFieldsForBackend.includes(key) && (!stringValue || stringValue.trim() === '')) ? '.' : value;
        formData.append(key, fieldValue)
      }
    });



    //create
    if (this.projectForm.valid && !this.project.id) {
      this.projectService.createProjects(formData, +this.strategy.id).subscribe(response => {
        this.isProjectSubmitted = false;
        this.isgoalAndIndicatorFormSubmitted = false;
        this.toastr.success(this.dir == 'rtl' ? 'تمت الاضافة بنجاح' : 'Added Successfully', 'نجاح')
        this.projectStrategicLinkItems = [];
        this.submitSuccess.emit({ projectId: response['data']['entity']['id'], strategyId: this.strategy.id })
        this.router.navigateByUrl(`strategies/${this.strategy.id}/projects/${response['data']['entity']['id']}`)
      });
    }
    else if (this.projectForm.valid && this.project.id > 0) {
      // Show confirmation dialog before updating
      this.pendingUpdateData = formData;
      this.showEditConfirmDialog = true;
    }
    else {
      this.toastr.error(this.dir == 'rtl' ? 'راجع البيانات المدخلة' : 'Please check the data and complete it')
    }
  }

  confirmEdit() {
    if (this.pendingUpdateData && this.project.id > 0) {
      this.projectService.updateProject(this.pendingUpdateData, +this.strategy.id, this.project.id).subscribe((response: any) => {
        this.toastr.success(this.dir == 'rtl' ? 'تم التعديل  بنجاح' : 'Updated Successfully', 'نجاح')
        this.submitSuccess.emit({ projectId: this.project.id, strategyId: this.strategy.id })
        this.showEditConfirmDialog = false;
        this.pendingUpdateData = null;
      })
    }
  }

  cancelEdit() {
    this.showEditConfirmDialog = false;
    this.pendingUpdateData = null;
  }
  onGoalChange(goalControl: any) {
    const selectedGoalId = +goalControl.value;
    const filterdIndicators = this.indicatorsOptions.filter(x => x.strategic_goal_id == selectedGoalId);
    this.filterdIndicatorsOptions = [...filterdIndicators]
  }
  onPillarChange(indicatorControl: any) {
    const pillarId = +indicatorControl.value;
    const filterdGoals = this.goalsOptions.filter(x => x.pillar_id == pillarId);
    this.filterdGoalsOptions = [...filterdGoals];
  }
  isGovLinkEmpty(govLinkage: IProjectGovLinkItem) {
    return !govLinkage["goal_description"] && !govLinkage["goal_reference"] && !govLinkage["reference_file"]
  }
  getFilesMetaData(file: File) {
    const { name, type, size } = file;
    let filesMetaData = {} as any;
    switch (type) {
      case 'image/jpeg':
      case 'image/png':
        filesMetaData = { name, type, size, icon: "fa-solid fa-file-image", color: "gray", is_deleted: false };
        break;
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        filesMetaData = { name, type, size, icon: "fa-solid fa-file-word", color: "blue", is_deleted: false };
        break;
      case 'application/pdf':
        filesMetaData = { name, type, size, icon: "fa-solid fa-file-pdf", color: "red", is_deleted: false };
        break;
      case 'video/mp4':
      case 'video/x-msvideo':
      case 'video/quicktime':
      case 'video/x-matroska':
        filesMetaData = { name, type, size, icon: "fa-solid fa-file-video", color: "red", is_deleted: false };
        break;
      default:
        filesMetaData = { name, type, size, icon: "fa-solid fa-file", color: "gray", is_deleted: false };
    }
    return filesMetaData;
  }

  joinProject() {
    console.log("this.project.id", this.project.id);
    this.signalRService.joinProject(this.project.id);
  }

  leaveProject() {
    this.signalRService.leaveProject(this.project.id);
  }
}

